import h from 'virtual-dom/h';
import * as Actions from '../actions/actions';
import * as CaptureTools from '../utilities/capture-tools';
import { settings } from '../../../../settings/settings-util';
import {Store} from "redux";
import {AlmeState} from "../../../../wireup/AlmeState";
import {VNode, VChild, VTree} from "virtual-dom";
import {
    DataCollectionMetadata,
    DataCollectionPaneConfig,
    DataInput
} from "../models/DataCollectionPaneConfig";
import {buildComboBox} from "../elements/ComboBox";
import {buildTextField} from "../elements/TextField";
import {buildCheckboxGroup} from "../elements/CheckboxGroup";
import {buildDataListField, getSelectedOption, getShouldBeHidden} from "../elements/DataList";
import {Dictionary, Mutator} from "../../../../types/rendering";
import { buildCustomValidationsErrorElement } from "../elements/ErrorMessages";
import {updateOverlayFormFocus} from "../utilities/type";
import { FocusHook } from '../utilities/FocusHook';
import {enableInputBox, disableInputBox} from '../../../lib-inputbox-toggle/lib/lib-inputbox-toggle';

type Renderers = (config: DataCollectionPaneConfig) => VChild;


export class DataCollectionPane
{

    _settings: typeof settings;
    _store: Store<AlmeState> | null = null;
    _mutators: Mutator[];
    _childRenderers: Renderers[];
    _performCustomValidations: (metadata: DataCollectionMetadata, paneId: string) => boolean | undefined;


    //#region Public Interface

    constructor(passedSettings: typeof settings, mutators: Mutator[], childRenderers: Renderers[],
        performCustomValidations: (metadata: DataCollectionMetadata, paneId: string) => boolean = () => true) {
        this._settings = passedSettings;
        this._mutators = mutators;
        this._childRenderers = childRenderers;
        this._performCustomValidations = performCustomValidations;
    }

    initialize(store): void
    {
        this._store = store;
    }

    doesRender(config: DataCollectionPaneConfig): boolean
    {
        return config.type === this._settings.ResponseActions.displayFeedbackPanes.paneTypes.datacollection;
    }

    getRender() 
    {
        return (config: DataCollectionPaneConfig): VNode | undefined =>
        {
            if (!this.doesRender(config)) { return; }

            let parameters: Dictionary = { className: this._settings.classNames.dataCollectionFeedbackPane };
            parameters = this._mutators.reduce((currentParams, mutate) => mutate(currentParams), parameters);

            const { closeIcon, header, prompt, paneId, active, metadata } = config;

            parameters.attributes = { 'data-id': paneId, 'data-active': active };

            const closeIconNode = this.buildCloseIcon(closeIcon, metadata, paneId);
            const headerNode = this.buildHeader(header, paneId);
            const bodyNodes = this.buildBody(prompt, metadata, paneId);
            const childNodes = this._childRenderers.map(render => render(config));

            return h('div', parameters, [...childNodes, closeIconNode, headerNode, bodyNodes]);
        };
    }

    //#endregion



    //#region Builder Methods

    buildHeader(headerText: string, paneId: string): VNode
    {
        return h('div', { className: this._settings.classNames.feedbackHeader, attributes: { paneId } }, headerText);
    }

    buildBody(promptText: string, metadata: DataCollectionMetadata, paneId: string): VNode
    {
        const { dataCollectionValidateRequiredDesc, dataCollectionArea, feedbackBody, feedbackPrompt } = this._settings.classNames;
        const requiredText = this._settings.textConstants.dataCollectionRequiredTextDesc;

        const promptElement = h('div', { className: feedbackPrompt }, promptText);
        const bodyElements = this.buildBodyElements(metadata);
        const requiredFieldDesc = h('div', { className: dataCollectionValidateRequiredDesc }, requiredText);
        const customValidationsErrorElement = buildCustomValidationsErrorElement(metadata, this._settings);
        const formElement = h('div', { className: dataCollectionArea }, [bodyElements, requiredFieldDesc]);
        const buttonElement = this.buildButtonElements(metadata, paneId);

        return h('div',
            {
                className: feedbackBody,
                focusHook: new FocusHook(this),
            }, [promptElement, customValidationsErrorElement, formElement, buttonElement]
        );
    }

    buildBodyElements(metadata: DataCollectionMetadata): VTree[]
    {
        let onInput: () => void;
        let onBlur: () => void;

        return metadata.dataInputs.reduce((currentElements: VTree[], input: DataInput) =>
        {
            switch (input.type)
            {
                case "Combo":
                    onInput = (): void =>
                    {
                        if (metadata.enableSubmitButtonOnValidation) { this.getValidatedFieldValues(metadata); }
                    };
                    onBlur = (): void => {
                        setTimeout(() => {
                            updateOverlayFormFocus(false, this._store, settings);
                        }, 0);
                    };

                    return [...currentElements, ...buildComboBox(input, metadata.dataInputs, this._settings, onInput, onBlur)];

                case "DataList":
                    onInput = (): void =>
                    {
                        if (metadata.enableSubmitButtonOnValidation) { this.getValidatedFieldValues(metadata); }

                        this._store?.dispatch({ type: "Render" });
                    };
                    onBlur = (): void => {
                        setTimeout(() => {
                            updateOverlayFormFocus(false, this._store, settings);
                        }, 0);
                    };

                    return [...currentElements, ...buildDataListField(input, metadata.dataInputs, this._settings, onInput, onBlur)];

                case "Text":
                    onInput = (): void =>
                    {
                        if (metadata.enableSubmitButtonOnValidation) { this.getValidatedFieldValues(metadata); }
                        if (input.multiline === "true") { this.updateRemainingTextCount(); }
                    };
                    onBlur = (): void => {
                        setTimeout(() => {
                            updateOverlayFormFocus(false, this._store, settings);
                        }, 0);
                    };

                    return [...currentElements, ...buildTextField(input, metadata.dataInputs, this._settings, onInput, onBlur)];

                case "CheckboxGroup":
                    onInput = (): void =>
                    {
                        if (metadata.enableSubmitButtonOnValidation) { this.getValidatedFieldValues(metadata); }
                    };
                    onBlur = (): void => {
                        setTimeout(() => {
                            updateOverlayFormFocus(false, this._store, settings);
                        }, 0);
                    };

                    return [...currentElements, ...buildCheckboxGroup(input, this._settings, onInput, onBlur)];
            }
        }, []);
    }

    buildButtonElements(metadata: DataCollectionMetadata, paneId: string): VNode
    {
        const { dataCollectionCancelButton, dataCollectionSubmitButton, dataCollectionButtonArea } = this._settings.classNames;
        const cancelProps = { className: dataCollectionCancelButton, onclick: (): void => { this.cancelPane(metadata.nextPane, paneId); } };
        const submitProps = { className: dataCollectionSubmitButton, onclick: (): void => { this.submitPane(metadata, paneId); } } as Dictionary;
        if (metadata.enableSubmitButtonOnValidation) { submitProps.disabled = true; }

        const cancelButton = h('button', cancelProps, metadata.cancelText);
        const submitButton = h('button', submitProps, metadata.buttonText);

        return h('div', { className: dataCollectionButtonArea }, [submitButton, cancelButton]);
    }

    buildCloseIcon(closeIcon: boolean, metadata: DataCollectionMetadata, paneId: string): VChild
    {
        const properties =
        {
            className: this._settings.classNames.dataCollectionCloseButton,
            onclick: (): void => { this.cancelPane(metadata.nextPane, paneId); },
        };

        return closeIcon ? h('button', properties, []) : [];
    }

    //#endregion



    //#region Action Methods

    updateSubmitButtonToState(enabled: boolean): void
    {
        const button = this.getFormPane()?.querySelector(`.${this._settings.classNames.dataCollectionSubmitButton}`);

        if (!button) { return; }

        if (enabled)
        {
            button.removeAttribute("disabled");
        }
        else
        {
            button.setAttribute("disabled", "true");
        }
    }

    updateRemainingTextCount(): void
    {
        const { textAreaCharacterCounter, dataCollectionPaneTextBox } = this._settings.classNames;

        const textCountArea = document.querySelector(`.${textAreaCharacterCounter}`);
        const textarea = document.querySelector(`textarea.${dataCollectionPaneTextBox}`);

        if (!(textarea instanceof HTMLTextAreaElement) || !(textCountArea instanceof HTMLDivElement)) { return; }

        const textLength = textarea.value.length;
        textCountArea.innerHTML = `${textLength}/${textarea.maxLength}`;
        textCountArea.setAttribute('aria-label', `${textLength}/${textarea.maxLength}`);
    }

    submitPane(metadata: DataCollectionMetadata, paneId: string): void
    {   
        disableInputBox(this._store, this._settings, () => true);
        this.clearErrorMessages();
        const formInputs = this.getValidatedFieldValues(metadata);

        const validationErrorElement = document.querySelector(
            `input[data-format-error="true"],input[data-required-error="true"],
             select[data-format-error="true"],select[data-required-error="true"]`
        ) as HTMLElement;
        validationErrorElement?.focus();        
        
        if (!this.allInputsAreValid(formInputs, metadata.dataInputs)) { return; }

        if (this._performCustomValidations) {
            const isFormValid = this._performCustomValidations(metadata, paneId);
            if (!isFormValid) {
                this.getFormPane()?.setAttribute('custom-validations-error', 'true');
                return;
            }
            else {
                this.getFormPane()?.removeAttribute('custom-validations-error');
            }
        }

        const { capturedFeedback } = this._settings.stateLocations.History;
        const { category, actions } = this._settings.Analytics.Feedback;

        CaptureTools.updateFeedback(this._store, capturedFeedback, paneId,
        {
            category,
            action: actions.completePane,
            label: formInputs,
            value: paneId,
        });

        this.moveToNextPane(metadata.nextPane, paneId, actions.completeFeedback);
    }


    getValidatedFieldValues(metadata: DataCollectionMetadata): {[key: string]: unknown}
    {
        const inputs = metadata.dataInputs.reduce((currentInputs: Dictionary, input: DataInput) =>
        {
            if (!input.id) { return currentInputs; }

            const element = document.querySelector(`[id=${input.id}]`);

            switch (input.type)
            {
                case "Combo":
                    if (element instanceof HTMLSelectElement && this.validate(element))
                    {
                        currentInputs[input.id] = element.options[element.selectedIndex].value;
                    }

                    return currentInputs;

                case "DataList":
                    if (element instanceof HTMLInputElement)
                    {
                        const selectedOption = getSelectedOption(element, input);

                        if (getShouldBeHidden(input, metadata.dataInputs))
                        {
                            currentInputs[input.id] = "";
                        }
                        else if (selectedOption)
                        {
                            currentInputs[input.id] = selectedOption.value;
                        }
                    }

                    return currentInputs;

                case "Text":
                    if ((element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) && this.validate(element))
                    {
                        currentInputs[input.id] = element.value;
                    }

                    return currentInputs;

                case "CheckboxGroup":
                    if (element instanceof HTMLDivElement && this.validateCheckBoxSelection(element))
                    {
                        const selectedCheckboxes = document.querySelectorAll(`input[name="checkbox_${input.id}"]:checked`);
                        currentInputs[input.id] = Array.from(selectedCheckboxes).map(checkbox => (checkbox as HTMLInputElement).value);
                    }

                    return currentInputs;   
            }
        }, {} as Dictionary);

        if (metadata.enableSubmitButtonOnValidation)
        {
            this.updateSubmitButtonToState(this.allInputsAreValid(inputs, metadata.dataInputs));
        }

        return inputs;
    }

    cancelPane(nextPaneId: string, currentPaneId: string): void
    {
        const { capturedFeedback } = this._settings.stateLocations.History;
        const { category, actions } = this._settings.Analytics.Feedback;

        CaptureTools.updateFeedback(this._store, capturedFeedback, currentPaneId,
        {
            category,
            action: actions.abortPane,
            label: {},
            value: currentPaneId,
        });

        this.moveToNextPane(nextPaneId, currentPaneId, actions.abortFeedback);
    }

    moveToNextPane(nextPaneId: string, currentPaneId: string, action: string): void
    {
        const {feedbackPanes, displayFeedback, capturedFeedback, capturedStageLocation} = this._settings.stateLocations.History;
        const { category } = this._settings.Analytics.Feedback;

        if (nextPaneId === this._settings.ResponseActions.displayFeedbackPanes.nextPaneEndKey)
        {
            CaptureTools.endFeedback(this._store, capturedFeedback, capturedStageLocation, currentPaneId, { category, action });
            this._store?.dispatch(Actions.endDisplayFeedbackPanes(feedbackPanes, displayFeedback));
        }
        else
        {
            this._store?.dispatch(Actions.setActiveFeedbackPane(nextPaneId, feedbackPanes));
        }
    }

    getFormPane(): HTMLDivElement | null
    {
        const windowRoot = this._settings.Window.rootId;
        const feedbackClass = this._settings.classNames.feedbackOverlay;
        const formPaneClass = this._settings.classNames.dataCollectionFeedbackPane;

        const formSelector = `#${windowRoot} div.${feedbackClass} .${formPaneClass}[data-active="true"]`;

        return document.querySelector(formSelector);
    }

    clearErrorMessages(): void
    {
        this.getFormPane()?.removeAttribute('data-required-error');
        this.getFormPane()?.removeAttribute('data-format-error');
    }

    //#endregion


    //#region Validation Methods

    validate(element: HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement): boolean
    {
        const isRequiredValid = this.validateRequired(element);
        const isPatternValid = this.validatePattern(element);

        return isRequiredValid && isPatternValid;
    }

    validateRequired(element: HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement): boolean
    {
        let isValid = true;

        if (element.required) {
            if (element instanceof HTMLSelectElement)
            {
                isValid = element.options[element.selectedIndex].value !== "";
            }
            else if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement)
            {
                isValid = element.required && (element.value.trim() !== '');
            }
        }

        if (!isValid)
        {
            this.getFormPane()?.setAttribute('data-required-error', 'true');
            element.setAttribute('data-required-error', 'true');
        }
        else
        {
            element.removeAttribute('data-required-error');
        }

        return isValid;
    }

    validatePattern(element: HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement): boolean
    {
        const patternText = element.getAttribute('pattern');

        if (!patternText) { return true; }

        const pattern = new RegExp(patternText);
        const isValid = pattern.test(element.value);

        if (!isValid)
        {
            this.getFormPane()?.setAttribute('data-format-error', 'true');
            element.setAttribute('data-format-error', 'true');
        }
        else
        {
            element.removeAttribute('data-format-error');
        }

        return isValid;
    }

    validateCheckBoxSelection(element: HTMLDivElement): boolean {
        let isValid = true;

        const minSelectionsField = document.querySelector(`#${element.id}_minSelections`);
        const maxSelectionsField = document.querySelector(`#${element.id}_maxSelections`);

        if (minSelectionsField || maxSelectionsField) {
            let minSelections = Number((minSelectionsField as HTMLInputElement)?.value);
            minSelections = isNaN(minSelections) ? 0 : minSelections;

            const checkboxes = Array.from(document.getElementsByName('checkbox_' + element.id));

            if (checkboxes) {
                let maxSelections = Number((maxSelectionsField as HTMLInputElement)?.value);
                maxSelections = isNaN(maxSelections) ? checkboxes.length : maxSelections;

                const selectedCheckboxes = checkboxes.filter(checkbox => (checkbox as HTMLInputElement).checked)
                isValid = selectedCheckboxes.length >= minSelections && selectedCheckboxes.length <= maxSelections;
            }
        }

        if (!isValid) {
            this.getFormPane()?.setAttribute('data-required-error', 'true');
            element.setAttribute('data-required-error', 'true');
        }
        else {
            element.removeAttribute('data-required-error');
        }

        return isValid;
    }

    allInputsAreValid(resultInputs: Dictionary, allInputs: DataInput[]): boolean
    {
        return Object.keys(resultInputs).length === allInputs.length;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-feedback-overlay/src/renderers/DataCollectionPane.ts